import { useState, useEffect, useCallback } from 'react';
import {
  getUserInfo,
  getUserSubscriptions,
  getUserPaymentInfo,
  getBillingHistory,
  getInvoices,
  getTokenUsageHistory,
  getTokenUsageByPeriod
} from '../../apiLayer/dashBoardAPIs/vsCodeDashboardAPIs';
import { getUserInfoFromAuth, getBYOKStatus } from '../../apiLayer/vsCodeAuthAPIs/authAPIs';
import { getBalance } from '../../apiLayer/dashBoardAPIs/paygAPIs';
import { getMyTeam } from '../../apiLayer/dashBoardAPIs/teamDashboardAPIs';

// User type constants
export const USER_TYPES = {
  INDIVIDUAL: 'individual',
  TEAM_ADMIN: 'team_admin',
  TEAM_MEMBER: 'team_member'
};

// Helper to check if error is an authorization error
const isAuthError = (err) => {
  const status = err?.response?.status;
  return status === 401 || status === 403;
};

export const useVsCodeDashboard = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [usageHistory, setUsageHistory] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [billingHistory, setBillingHistory] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  // Plan type state
  const [planType, setPlanType] = useState('fixed');

  // Team-related state
  const [userType, setUserType] = useState(USER_TYPES.INDIVIDUAL);
  const [teamId, setTeamId] = useState(null);

  // Check team membership using /teams/my-team endpoint
  const checkTeamMembership = useCallback(async (currentUserId, currentUserEmail) => {
    try {
      console.log('=== Checking Team Membership ===');
      console.log('Current user ID:', currentUserId);
      console.log('Current user email:', currentUserEmail);

      const teamData = await getMyTeam();
      console.log('Team data from /teams/my-team:', JSON.stringify(teamData, null, 2));

      if (teamData) {
        // Set team ID for reference
        setTeamId(teamData.id);

        // Find current user in members array and check their role
        const members = teamData.members || [];
        const userEmail = currentUserEmail?.toLowerCase();

        // Find the current user in the members list by email or user_id
        const currentMember = members.find(member =>
          member.email?.toLowerCase() === userEmail ||
          member.user_id === currentUserId
        );

        console.log('Current member found:', currentMember);

        if (currentMember) {
          const role = currentMember.role?.toLowerCase();
          console.log('Current user role:', role);

          if (role === 'admin') {
            setUserType(USER_TYPES.TEAM_ADMIN);
            console.log('User Type (from teams endpoint): Team Admin');
          } else {
            setUserType(USER_TYPES.TEAM_MEMBER);
            console.log('User Type (from teams endpoint): Team Member');
          }
        } else {
          // User not found in members but has team data - treat as member
          console.log('User not found in members array, defaulting to member');
          setUserType(USER_TYPES.TEAM_MEMBER);
        }

        console.log('================================');
        return teamData;
      }
    } catch (err) {
      // 404 or error means user is not part of a team - this is expected for individuals
      console.log('User is not part of any team (individual user)');
      setUserType(USER_TYPES.INDIVIDUAL);
      setTeamId(null);
      console.log('================================');
      return null;
    }
  }, []);

  // Fetch user information from auth endpoint
  const fetchUserInfoFromAuth = useCallback(async () => {
    try {
      const data = await getUserInfoFromAuth();
      setUserInfo(data);

      // Extract user ID from the response
      const currentUserId = data?.id || data?.user_id;
      if (currentUserId) {
        setUserId(currentUserId);
        console.log('User ID:', currentUserId);
      }

      // Debug: Log team-related fields from backend
      console.log('=== User Info Debug ===');
      console.log('is_team:', data?.is_team);
      console.log('team_id:', data?.team_id);
      console.log('user_id:', currentUserId);
      console.log('subscriber:', data?.subscriber);
      console.log('Full user data:', JSON.stringify(data, null, 2));
      console.log('=======================');

      // Get current user's email for comparison
      const currentUserEmail = data?.email;

      // Determine user type based on is_team and team_id from auth endpoint
      if (data?.is_team && data?.team_id) {
        setTeamId(data.team_id);
        // If team_id matches user's own ID, they are the admin
        if (data.team_id === currentUserId) {
          setUserType(USER_TYPES.TEAM_ADMIN);
          console.log('User Type: Team Admin');
        } else {
          setUserType(USER_TYPES.TEAM_MEMBER);
          console.log('User Type: Team Member');
        }
      } else {
        // Auth endpoint doesn't have team info, check /teams/my-team endpoint
        console.log('Auth endpoint has no team info, checking /teams/my-team...');
        await checkTeamMembership(currentUserId, currentUserEmail);
      }

      return data;
    } catch (err) {
      console.error('Error fetching user info from auth:', err);
      // Auth endpoint failure is critical - always show error for auth issues
      if (isAuthError(err)) {
        setError('Session expired. Please sign in again.');
      } else {
        setError('Unable to load user information. Please try again.');
      }
      throw err;
    }
  }, [checkTeamMembership]);

  // Fetch user information (legacy method, kept for compatibility)
  const fetchUserInfo = useCallback(async () => {
    if (!userId) return;

    try {
      const data = await getUserInfo(userId);
      setUserInfo(data);
    } catch (err) {
      console.error('Error fetching user info:', err);
      if (isAuthError(err)) {
        setError('Session expired. Please sign in again.');
      }
    }
  }, [userId]);

  // Lifetime users own the product outright — they have NO subscription, so the
  // unified subscription endpoint reports them as non-subscribers and the
  // dashboard would otherwise render them as free-trial users. The payments
  // service is the source of truth: GET /payment/get_user_info?id={user_id}
  // returns users_plan_name === 'lifetime' for lifetime owners. Merge that into
  // userInfo so every view can check userInfo.users_plan_name.
  const fetchPlanFromPayments = useCallback(async (uid) => {
    if (!uid) return null;
    try {
      const data = await getUserInfo(uid);
      if (data?.users_plan_name) {
        setUserInfo((prev) => ({
          ...(prev || {}),
          users_plan_name: data.users_plan_name,
          subscriber: data.subscriber,
        }));
      }
      return data;
    } catch (err) {
      console.error('Error fetching plan info from payments service:', err);
      return null;
    }
  }, []);

  // Fetch user subscriptions
  const fetchSubscriptions = useCallback(async () => {
    try {
      const data = await getUserSubscriptions();
      setSubscriptions(data);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      if (isAuthError(err)) {
        setError('Session expired. Please sign in again.');
      }
    }
  }, []);

  // Fetch user payment information
  const fetchPaymentInfo = useCallback(async () => {
    try {
      const data = await getUserPaymentInfo();
      setPaymentInfo(data);
    } catch (err) {
      console.error('Error fetching payment info:', err);
      // Don't treat "no payment info" as a dashboard-breaking error
      if (err.response?.data?.detail?.required_action === 'ADD_CARD') {
        console.log('User has no payment info - showing upgrade option');
        setPaymentInfo(null); // Set to null to indicate no payment info
      } else if (isAuthError(err)) {
        setError('Session expired. Please sign in again.');
      }
      // Other errors are logged but don't block the dashboard
    }
  }, []);

  // Fetch usage history for charts (new analytics endpoint)
  const fetchUsageHistory = useCallback(async () => {
    try {
      const data = await getTokenUsageHistory();
      setUsageHistory(data);
      return data;
    } catch (err) {
      console.error('Error fetching usage history:', err);
      if (isAuthError(err)) {
        setError('Session expired. Please sign in again.');
      }
    }
  }, []);

  // Fetch usage by period for card displays (new analytics endpoint)
  // period: 'total', 'month', 'year', 'week', 'day'
  const fetchPeriodUsage = useCallback(async (period) => {
    try {
      const data = await getTokenUsageByPeriod(period);
      return data?.data || 0;
    } catch (err) {
      console.error('Error fetching period usage:', err);
      return 0;
    }
  }, []);

  // Fetch billing history
  const fetchBillingHistory = useCallback(async () => {
    try {
      const data = await getBillingHistory();
      setBillingHistory(data);
    } catch (err) {
      console.error('Error fetching billing history:', err);
      if (isAuthError(err)) {
        setError('Session expired. Please sign in again.');
      }
    }
  }, []);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    try {
      const data = await getInvoices();
      setInvoices(data);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      if (isAuthError(err)) {
        setError('Session expired. Please sign in again.');
      }
    }
  }, []);

  // Detect plan type by probing BYOK, PAYG, and subscription endpoints
  const detectPlanType = useCallback(async () => {
    try {
      const [byokResult, paygResult, subResult] = await Promise.allSettled([
        getBYOKStatus(),
        getBalance(),
        getUserSubscriptions()
      ]);

      // BYOK check disabled — BYOK billing view removed from dashboard routing.
      // Keeping code for reference; getBYOKStatus() still called above to avoid
      // changing the destructuring.
      // if (byokResult.status === 'fulfilled' && byokResult.value?.subscription) {
      //   const sub = byokResult.value.subscription;
      //   const byokSubType = sub.subscription_type?.toLowerCase();
      //   if (['monthly', 'yearly', 'lifetime'].includes(byokSubType)) {
      //     setPlanType('byok');
      //     return 'byok';
      //   }
      // }

      // 1. PAYG: user has actually purchased or loaded credits
      //    (not just a default $0 balance every new user gets)
      if (paygResult.status === 'fulfilled' && paygResult.value) {
        const paygData = paygResult.value;
        if (paygData.balance > 0.1) {
          setPlanType('sixth_credit');
          return 'sixth_credit';
        }
      }

      // 2. Fixed subscription: user has active Pro/Pro+ subscription
      if (subResult.status === 'fulfilled' && subResult.value?.is_subscriber &&
          subResult.value?.active_subscription) {
        setPlanType('fixed');
        return 'fixed';
      }

      // Default → fixed (new users, trial users, etc.)
      setPlanType('fixed');
      return 'fixed';
    } catch {
      setPlanType('fixed');
      return 'fixed';
    }
  }, []);

  // Initialize dashboard data
  const initializeDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // First, get user info from auth endpoint to get the user ID
      // This is critical - if it fails, we can't load the dashboard
      const userData = await fetchUserInfoFromAuth();
      if (userData && (userData.id || userData.user_id)) {
        // Once we have the user ID, fetch the rest of the data
        // These are non-critical - errors are handled individually and won't block dashboard
        await Promise.all([
          fetchPlanFromPayments(userData.id || userData.user_id),
          fetchSubscriptions(),
          fetchPaymentInfo(),
          fetchUsageHistory(),
          detectPlanType(),
          fetchBillingHistory(),
          fetchInvoices()
        ]);
      }
    } catch (err) {
      // Error already set by fetchUserInfoFromAuth if it failed
      // Don't override with generic message
      console.error('Dashboard initialization failed:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchUserInfoFromAuth, fetchPlanFromPayments, fetchSubscriptions, fetchPaymentInfo, fetchUsageHistory, detectPlanType, fetchBillingHistory, fetchInvoices]);

  useEffect(() => {
    initializeDashboard();
  }, [initializeDashboard]);

  return {
    // User and team info
    userInfo,
    userType,
    teamId,
    userId,

    // Plan type
    planType,

    // Subscription and billing
    subscriptions,
    paymentInfo,
    billingHistory,
    invoices,

    // Token usage (new analytics endpoints)
    usageHistory,

    // State
    loading,
    error,

    // Methods
    fetchUserInfo,
    fetchSubscriptions,
    fetchPaymentInfo,
    fetchUsageHistory,
    fetchPeriodUsage,
    initializeDashboard,
    fetchBillingHistory,
    fetchInvoices
  };
};
